import React, { useState, useMemo, useCallback } from "react";
import {
  Checkbox,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  OutlinedInput,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export default function MultiSelectWithSearch({
  options = [],
  values = [],
  onChange = () => {},
  placeholderText = "選択してください",
  labelText = "選択してください",
  checkAllText = "すべて選択",
  ...props
}) {
  const [searchText, setSearchText] = useState("");

  const allSelected = values.length === options.length;
  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      opt?.label?.toLowerCase()?.includes(searchText.toLowerCase())
    );
  }, [searchText, options]);

  const handleSelectAllClick = useCallback(() => {
    if (allSelected) {
      onChange([]);
    } else {
      const allValues = options.map((opt) => opt.value);
      onChange(allValues);
    }
  }, [allSelected, options, onChange]);

  const handleOptionClick = useCallback(
    (value) => {
      const newValues = values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value];

      onChange(newValues);
    },
    [values, onChange]
  );

  return (
    <Select
      multiple
      displayEmpty
      label={labelText}
      value={values}
      input={<OutlinedInput />}
      renderValue={(selected) =>
        selected.length === 0
          ? "選択してください"
          : `${selected.length}項目が選択されました`
      }
      {...props}
    >
      <MenuItem onClick={handleSelectAllClick}>
        <Checkbox
          checked={allSelected}
          indeterminate={values.length > 0 && !allSelected}
        />
        <span
          style={{
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {checkAllText}
        </span>
        <Box sx={{ flexGrow: 1 }} />
        <span>{options.reduce((acc, opt) => acc + opt.count, 0)}</span>
      </MenuItem>
      <TextField
        placeholder={placeholderText}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        fullWidth
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1,
          borderTop: "1px solid #ccc",
          borderBottom: "1px solid #ccc",
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              border: "none",
            },
          },
        }}
      />
      <Box sx={{ maxHeight: 190, overflowY: "auto" }}>
        {filteredOptions.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            onClick={() => handleOptionClick(opt.value)}
          >
            <Checkbox
              checked={values.indexOf(opt.value) > -1}
              onChange={() => handleOptionClick(opt.value)}
            />
            <span
              style={{
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {opt.label}
            </span>
            <Box sx={{ flexGrow: 1 }} />
            <span>{opt.count}</span>
          </MenuItem>
        ))}
      </Box>
    </Select>
  );
}
